import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../config/firebaseConfig';
import { API_BASE_URL } from '../../config/apiConfig';
import CustomAlertModal from '../modals/CustomAlertModal';

const ADJECTIVES = ['잠자는', '졸린', '배고픈', '신나는', '조용한', '활발한', '느긋한', '귀여운', '용감한', '엉뚱한', '수줍은', '씩씩한', '게으른', '바쁜', '행복한'];
const ANIMALS = ['사자', '하마', '고양이', '강아지', '펭귄', '판다', '토끼', '여우', '곰', '원숭이', '코끼리', '기린', '늑대', '호랑이', '다람쥐'];
const generateRandomName = () => {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const animal = ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
  return `${adj} ${animal}`;
};

const RegisterForm = ({ onClose, onRegisterSuccess, onLoginClick, onLoginSuccess, preVerifiedEmail }) => {
  // 이름: localStorage에 저장해 탭 재오픈/링크 클릭 후에도 같은 이름 유지
  const [name, setName] = useState(() => {
    const saved = localStorage.getItem('pendingRegisterName');
    if (saved) return saved;
    const newName = generateRandomName();
    localStorage.setItem('pendingRegisterName', newName);
    return newName;
  });
  const [email, setEmail] = useState(preVerifiedEmail || '');
  const [usePassword, setUsePassword] = useState(true);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [passwordMatchError, setPasswordMatchError] = useState(false);

  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(!!preVerifiedEmail);
  const [codeVerified, setCodeVerified] = useState(!!preVerifiedEmail);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const isRegisteringRef = useRef(false); // ref guard: state 업데이트 전 중복 호출 차단
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailExists, setEmailExists] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({
    email: false, password: false, password2: false,
  });

  const [alertModal, setAlertModal] = useState({
    isOpen: false, title: '', message: '', type: 'info', showCancel: false, onConfirm: null
  });

  const showAlert = useCallback((message, type = 'info', title = '', showCancel = false, onConfirm = null) => {
    setAlertModal({ isOpen: true, title, message, type, showCancel, onConfirm });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!email) errors.email = '이메일은 필수입니다.';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = '유효한 이메일 주소를 입력해주세요.';
    if (usePassword) {
      if (!password) errors.password = '비밀번호는 필수입니다.';
      else if (password.length < 6 || password.length > 128) errors.password = '비밀번호는 6-128자 사이여야 합니다.';
    }
    setValidationErrors(errors);
    return errors;
  }, [name, email, password, usePassword]);

  useEffect(() => {
    if (!usePassword || password2 === '') setPasswordMatchError(false);
    else setPasswordMatchError(password !== password2);
    validateForm();
  }, [email, password, password2, usePassword, validateForm]);

  // 이메일 변경 시 emailExists 초기화
  useEffect(() => {
    setEmailExists(false);
  }, [email]);

  // preVerifiedEmail이 비동기로 도착한 경우 처리
  useEffect(() => {
    if (!preVerifiedEmail) return;
    setEmail(preVerifiedEmail);
    setCodeSent(true);
    setCodeVerified(true);
    setVerificationCode('');
  }, [preVerifiedEmail]);

  // 다른 탭에서 이메일 인증 완료 시 자동 반영
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'emailVerified') return;
      try {
        const { email: verifiedEmail } = JSON.parse(e.newValue);
        if (!verifiedEmail) return;
        setEmail(verifiedEmail);
        setCodeSent(true);
        setCodeVerified(true);
        setVerificationCode('');
      } catch {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // 서버 폴링: 다른 브라우저(인앱 브라우저 등)에서 이메일 링크 클릭 인증 감지
  useEffect(() => {
    if (!codeSent || codeVerified || !email) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/check-email-verified?email=${encodeURIComponent(email)}`);
        const data = await res.json();
        if (data.verified) {
          setCodeVerified(true);
          setVerificationCode('');
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [codeSent, codeVerified, email]);

  // 쿨다운 타이머
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleBlur = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const isFormValid = email
    && (!usePassword || (password && password2 && !passwordMatchError))
    && Object.keys(validationErrors).length === 0;

  // 이메일 옆 "전송" 버튼
  const handleSendCode = async () => {
    setTouched(prev => ({ ...prev, email: true }));
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showAlert('유효한 이메일을 입력해주세요.', 'warning', '입력 오류'); return;
    }
    setIsSendingCode(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const pendingInvite = localStorage.getItem('pendingInviteCode');
      const res = await fetch(`${API_BASE_URL}/api/auth/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...(pendingInvite ? { inviteCode: pendingInvite } : {}) }),
        signal: controller.signal,
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.msg === '이미 사용 중인 이메일입니다.') {
          setEmailExists(true);
          return;
        }
        showAlert(data.msg || '이메일 발송에 실패했습니다.', 'error', '발송 실패');
        return;
      }
      setEmailExists(false);
      setCodeSent(true);
      setCodeVerified(false);
      setVerificationCode('');
      setResendCooldown(60);
    } catch (e) {
      if (e.name === 'AbortError') {
        showAlert('서버 응답 시간이 초과됐습니다. 잠시 후 다시 시도해주세요.', 'error', '시간 초과');
      } else {
        showAlert('네트워크 오류가 발생했습니다.', 'error', '오류');
      }
    } finally {
      clearTimeout(timeout);
      setIsSendingCode(false);
    }
  };

  // 재발송
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setIsSendingCode(true);
    try {
      const pendingInvite = localStorage.getItem('pendingInviteCode');
      const res = await fetch(`${API_BASE_URL}/api/auth/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...(pendingInvite ? { inviteCode: pendingInvite } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) { showAlert(data.msg || '재발송에 실패했습니다.', 'error', '발송 실패'); return; }
      setResendCooldown(60);
      setVerificationCode('');
      setCodeVerified(false);
      showAlert('인증 코드를 다시 발송했습니다.', 'success', '발송 완료');
    } catch (e) {
      showAlert('네트워크 오류가 발생했습니다.', 'error', '오류');
    } finally {
      setIsSendingCode(false);
    }
  };

  // 인증 코드 확인 버튼
  const handleVerifyCode = async () => {
    if (verificationCode.length !== 2) return;
    setIsVerifying(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(data.msg || '인증에 실패했습니다.', 'error', '인증 실패');
        return;
      }
      setCodeVerified(true);
    } catch (e) {
      showAlert('네트워크 오류가 발생했습니다.', 'error', '오류');
    } finally {
      setIsVerifying(false);
    }
  };

  // 회원가입 버튼
  const handleRegister = async () => {
    setTouched({ name: true, email: true, password: true, password2: true });
    const currentErrors = validateForm();
    if (usePassword && !password2) { showAlert('비밀번호 확인을 입력해주세요.', 'warning', '입력 오류'); return; }
    if (Object.keys(currentErrors).length > 0 || passwordMatchError) {
      showAlert('모든 필수 정보를 올바르게 입력해주세요.', 'warning', '입력 오류'); return;
    }
    if (!codeVerified) { showAlert('이메일 인증을 먼저 완료해주세요.', 'warning', '인증 필요'); return; }

    if (isRegisteringRef.current) return; // 중복 호출 방지
    isRegisteringRef.current = true;
    setIsRegistering(true);
    try {
      const pendingInvite = localStorage.getItem('pendingInviteCode');
      const body = { name, email, verificationCode, ...(pendingInvite ? { inviteCode: pendingInvite } : {}) };
      if (usePassword && password) body.password = password;

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.errors) {
          const serverErrors = {};
          data.errors.forEach(err => { serverErrors[err.field] = err.message; });
          setValidationErrors(prev => ({ ...prev, ...serverErrors }));
        }
        showAlert(data.msg || '회원가입에 실패했습니다.', 'error', '회원가입 실패');
        return;
      }

      // 회원가입 완료 → 임시 저장 데이터 정리
      localStorage.removeItem('pendingRegisterName');
      localStorage.removeItem('pendingInviteCode'); // 가입 완료 후 삭제 (Safari에 남아 가입폼 재표시 방지)

      // 서버가 발급한 customToken으로 자동 로그인
      if (data.customToken && onLoginSuccess) {
        const credential = await signInWithCustomToken(auth, data.customToken);
        const idToken = await credential.user.getIdToken();
        const loginRes = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        });
        if (loginRes.ok) {
          const loginData = await loginRes.json();
          onLoginSuccess(loginData.user, 'general');
          return;
        }
      }

      // fallback: 로그인 화면으로 이동
      showAlert('회원가입이 완료되었습니다!\n로그인 해주세요.', 'success', '회원가입 성공', false, () => {
        closeAlert();
        onLoginClick();
      });
    } catch (error) {
      showAlert(`회원가입 실패: ${error.message}`, 'error', '회원가입 실패');
    } finally {
      isRegisteringRef.current = false;
      setIsRegistering(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white w-11/12 max-w-md rounded-lg shadow-xl p-6">
        <div className="relative mb-4">
          <h2 className="text-xl font-bold text-gray-800 text-center">회원가입</h2>
          <button onClick={onLoginClick} className="absolute top-0 left-0 text-blue-500 hover:text-blue-700 px-2 py-1 rounded-md">
            <ArrowLeft size={20} />
          </button>
        </div>

        {/* 초대 배너 */}
        {localStorage.getItem('pendingInviteCode') && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700 flex items-center gap-2">
            🎉 <span>초대를 받으셨군요! 가입 후 자동으로 방에 입장됩니다.</span>
          </div>
        )}

        <div className="space-y-4">
          {/* 이름 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">닉네임 <span className="text-xs text-gray-400">(나중에 설정에서 변경 가능)</span></label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={name}
              onChange={(e) => { setName(e.target.value); localStorage.setItem('pendingRegisterName', e.target.value); }}
              placeholder="닉네임을 입력하거나 그대로 사용하세요"
            />
          </div>

          {/* 이메일 + 전송 버튼 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
            <div className="flex space-x-2">
              <input
                type="email"
                className={`flex-1 min-w-0 border ${touched.email && validationErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2`}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setCodeSent(false); setCodeVerified(false); setVerificationCode(''); }}
                onBlur={() => handleBlur('email')}
                placeholder="example@email.com"
              />
              <button
                onClick={codeSent && resendCooldown > 0 ? handleResendCode : handleSendCode}
                disabled={isSendingCode || (codeSent && resendCooldown > 0)}
                className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                  isSendingCode || (codeSent && resendCooldown > 0)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : codeSent
                      ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {isSendingCode ? '발송 중...' : codeSent && resendCooldown > 0 ? `${resendCooldown}초` : codeSent ? '재발송' : '전송'}
              </button>
            </div>
            {touched.email && validationErrors.email && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
            )}
            {emailExists && (
              <div className="flex items-center gap-2 mt-2 bg-amber-50 border border-amber-300 rounded-md px-3 py-2">
                <span className="text-amber-700 text-sm">이미 가입된 이메일입니다.</span>
                <button
                  type="button"
                  onClick={onLoginClick}
                  className="ml-auto text-blue-600 text-sm font-semibold underline whitespace-nowrap"
                >
                  로그인하기 →
                </button>
              </div>
            )}
          </div>

          {/* 인증 코드 입력: 링크 버튼으로 인증한 경우 숨김, 완료 메시지만 표시 */}
          {codeVerified && codeSent && !verificationCode ? (
            <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-md px-3 py-2">
              <span className="text-green-600 text-sm font-medium">✓ 이메일 인증이 완료되었습니다.</span>
            </div>
          ) : codeSent && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                인증 코드 <span className="text-blue-500 font-normal text-xs">({email}로 발송됨)</span>
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  placeholder="2자리 코드 입력"
                  className={`flex-1 min-w-0 border ${
                    codeVerified
                      ? 'border-green-500 bg-green-50'
                      : verificationCode.length > 0 && verificationCode.length < 2
                        ? 'border-orange-400'
                        : 'border-gray-300'
                  } rounded-md px-3 py-2 text-center text-lg font-mono tracking-widest`}
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 2));
                    setCodeVerified(false);
                  }}
                  disabled={codeVerified}
                  autoFocus
                />
                <button
                  onClick={handleVerifyCode}
                  disabled={verificationCode.length !== 2 || isVerifying || codeVerified}
                  className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                    codeVerified
                      ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                      : verificationCode.length !== 2 || isVerifying
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {isVerifying ? '확인 중...' : codeVerified ? '✓ 인증됨' : '인증'}
                </button>
              </div>
              {codeVerified && (
                <p className="text-green-600 text-xs mt-1">✓ 이메일 인증이 완료되었습니다.</p>
              )}
            </div>
          )}

          {/* 비밀번호 설정 (필수) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호</label>
            <input
              type="password"
              className={`w-full border ${touched.password && validationErrors.password ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
            />
            {touched.password && validationErrors.password && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 확인</label>
            <input
              type="password"
              className={`w-full border ${touched.password2 && passwordMatchError ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2`}
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              onBlur={() => handleBlur('password2')}
            />
            {touched.password2 && passwordMatchError && (
              <p className="text-red-500 text-xs mt-1">비밀번호가 일치하지 않습니다.</p>
            )}
          </div>
        </div>

        {/* 회원가입 버튼 */}
        <div className="mt-6">
          <button
            onClick={handleRegister}
            disabled={!isFormValid || !codeVerified || isRegistering}
            className={`w-full px-4 py-2 rounded-md text-white font-semibold ${
              isFormValid && codeVerified && !isRegistering
                ? 'bg-blue-500 hover:bg-blue-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {isRegistering ? '회원가입 중...' : '회원가입'}
          </button>
        </div>

        <CustomAlertModal
          isOpen={alertModal.isOpen}
          onClose={closeAlert}
          onConfirm={alertModal.onConfirm}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          showCancel={alertModal.showCancel}
        />
      </div>
    </div>
  );
};

export default RegisterForm;
